import { AllFormData, amountKeys } from '../../../types/givingCheckoutForm';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import BlockRadioList from '../../components/BlockRadioList/BlockRadioList';
import CheckoutPreview from '../../components/CheckoutPreview/CheckoutPreview';
import DescriptionInput from '../../components/DescriptionInput/DescriptionInput';
import { PAGES } from '../../constants/pages';
import Steps from '../../components/Steps/Steps';
import { formatCurrency } from '../../utils/currencyFormatting';
import { useCharityData } from '../../context/charityContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useHeadingSetFocusRef } from '../../hooks/useHeadingSetFocusRef';
import { useTranslation } from 'react-i18next';

const CustomAmountDescriptions: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { charity } = useCharityData();
  const formMethods = useFormContext<AllFormData>();
  const { handleSubmit } = formMethods;

  const [
    customAmountDescriptionsEnabled,
    suggestedAmounts,
    suggestedAmountDefault,
    numberOfCustomAmounts,
  ] = useWatch<
    AllFormData,
    [
      'customAmountDescriptionsEnabled',
      'suggestedAmounts',
      'suggestedAmountDefault',
      'numberOfCustomAmounts',
    ]
  >({
    name: [
      'customAmountDescriptionsEnabled',
      'suggestedAmounts',
      'suggestedAmountDefault',
      'numberOfCustomAmounts',
    ],
  });

  const [activeAmount, setActiveAmount] = useState(suggestedAmountDefault);

  const headingRef = useHeadingSetFocusRef();

  const onSubmit = handleSubmit(async () => {
    navigate(`${PAGES.PostDonationDetails}${location.search}`);
  });

  useDocumentTitle(t('customAmount.descriptions.heading'));

  const amountDescriptionInputs = useMemo(
    () =>
      [...Array(Number(numberOfCustomAmounts))].map((_value, i) => {
        const fieldName = `amount${i + 1}`;
        const suggestedAmountValue =
          suggestedAmounts?.[fieldName as amountKeys]!;
        const suggestedAmount = parseInt(suggestedAmountValue);

        return (
          <DescriptionInput
            key={`description${fieldName}`}
            id={i + 1}
            label={t('customAmount.descriptions.label', {
              amount: formatCurrency({
                currencyCode: charity?.settlementCurrencyCode,
                amount: suggestedAmount,
              }),
            })}
            onFocus={() => setActiveAmount(`${i + 1}`)}
            onBlur={() => setActiveAmount(suggestedAmountDefault)}
          />
        );
      }),
    [
      numberOfCustomAmounts,
      charity?.settlementCurrencyCode,
      suggestedAmountDefault,
      suggestedAmounts,
      t,
    ],
  );

  return (
    <div className="giving-checkout-page">
      <div className="giving-checkout-page-form">
        <form noValidate method="post" onSubmit={onSubmit}>
          <Steps start={3} end={5} />
          <h1 className="jg-h3" ref={headingRef} tabIndex={-1}>
            {t('customAmount.descriptions.heading')}
          </h1>
          <p className="jg-space-mblg jg-font-weight-regular jg-color--empress">
            {t('customAmount.descriptions.description')}
          </p>

          <p className="jg-h6">{t('customAmount.descriptions.question')}</p>

          <BlockRadioList
            name="customAmountDescriptionsEnabled"
            items={[
              {
                title: t('common.no'),
                value: 'no',
              },
              {
                title: t('common.yes'),
                value: 'yes',
              },
            ]}
          />

          {customAmountDescriptionsEnabled === 'yes' && amountDescriptionInputs}

          <button
            className="jg-btn"
            type="submit"
            data-qa="customamounts-description-continue"
            data-track="customamounts-description-continue"
          >
            {t('common.continue')}
          </button>
          <button
            className="giving-checkout-button-link"
            type="button"
            onClick={() => navigate(`${PAGES.CustomAmounts}${location.search}`)}
          >
            {t('common.back')}
          </button>
        </form>
      </div>
      <div className="giving-checkout-page-preview">
        <CheckoutPreview
          previewBlock={['cta', 'frequency', 'input']}
          suggestedAmountDefault={activeAmount}
        />
      </div>
    </div>
  );
};

export default CustomAmountDescriptions;
